import { create } from 'zustand';
import { api } from '../services/apiService';
import type { Job, JobRun, JobStats, UsageToday } from '../types/jobs';

interface JobState {
  jobs: Job[];
  recentRuns: JobRun[];
  stats: JobStats | null;
  usage: UsageToday | null;
  selectedJob: Job | null;
  loading: boolean;
  artifact: string | null;
  artifactLoading: boolean;
  fetchJobs: () => Promise<void>;
  fetchStats: () => Promise<void>;
  fetchRecentRuns: () => Promise<void>;
  fetchJobRuns: (id: string, limit?: number) => Promise<JobRun[]>;
  fetchArtifact: (jobId: string, runId: number) => Promise<void>;
  clearArtifact: () => void;
  setSelectedJob: (job: Job | null) => void;
  acknowledgeWarning: (runId: number, jobId: string) => Promise<void>;
}

export const useJobStore = create<JobState>((set) => ({
  jobs: [],
  recentRuns: [],
  stats: null,
  usage: null,
  selectedJob: null,
  loading: false,
  artifact: null,
  artifactLoading: false,

  fetchJobs: async () => {
    try {
      const jobs = await api.get<Job[]>('/jobs');
      set({ jobs });
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
    }
  },

  fetchStats: async () => {
    try {
      const [stats, usage] = await Promise.all([
        api.get<JobStats>('/jobs/stats'),
        api.get<UsageToday>('/usage/today').catch(() => null),
      ]);
      set({ stats, usage });
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  },

  fetchRecentRuns: async () => {
    try {
      const recentRuns = await api.get<JobRun[]>('/jobs/runs/recent?limit=100');
      set({ recentRuns });
    } catch (err) {
      console.error('Failed to fetch recent runs:', err);
    }
  },

  fetchJobRuns: async (id: string, limit = 20) => {
    try {
      return await api.get<JobRun[]>(`/jobs/${id}/runs?limit=${limit}`);
    } catch (err) {
      console.error('Failed to fetch job runs:', err);
      return [];
    }
  },

  fetchArtifact: async (jobId: string, runId: number) => {
    set({ artifactLoading: true, artifact: null });
    try {
      const data = await api.get<{ content: string }>(`/jobs/${jobId}/runs/${runId}/artifact`);
      set({ artifact: data.content, artifactLoading: false });
    } catch (err) {
      console.error('Failed to fetch artifact:', err);
      set({ artifact: null, artifactLoading: false });
    }
  },

  clearArtifact: () => set({ artifact: null, artifactLoading: false }),

  setSelectedJob: (job: Job | null) => set({ selectedJob: job }),

  acknowledgeWarning: async (runId: number, jobId: string) => {
    // Optimistic update
    set((state) => ({
      recentRuns: state.recentRuns.map((r) =>
        r.id === runId ? { ...r, acknowledged: true } : r
      ),
    }));
    try {
      await api.post(`/warnings/${runId}/acknowledge`, { jobId });
    } catch (err) {
      console.error('Failed to acknowledge warning:', err);
      // Revert on failure
      set((state) => ({
        recentRuns: state.recentRuns.map((r) =>
          r.id === runId ? { ...r, acknowledged: false } : r
        ),
      }));
    }
  },
}));
